import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime } from "@/lib/statuts";
import { Building2, Phone, Mail, Loader2, History, PlusCircle, X, ArrowDownCircle, ArrowUpCircle, Bell, Save } from "lucide-react";
import { toast } from "sonner";

export default function Partenaires() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [ents, setEnts] = useState(null);
  const [soldes, setSoldes] = useState({});
  const [vtTypes, setVtTypes] = useState([]);
  const [histFor, setHistFor] = useState(null);
  const [creditFor, setCreditFor] = useState(null);
  const [paramsFor, setParamsFor] = useState(null);

  const loadSolde = (id) => api.get(`/partenaires/${id}/solde`).then((s) => setSoldes((prev) => ({ ...prev, [id]: s.data }))).catch(() => {});

  useEffect(() => {
    api.get("/entreprises").then((r) => { setEnts(r.data); r.data.forEach((e) => loadSolde(e.id)); });
    if (isAdmin) api.get("/vt-types?all=true").then((r) => setVtTypes(r.data)).catch(() => {});
  }, [isAdmin]);

  if (!ents) return <div className="flex h-96 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">Partenaires</h1>
        <p className="text-sm text-slate-500">{ents.length} installateur{ents.length > 1 ? "s" : ""} partenaire{ents.length > 1 ? "s" : ""}</p>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {ents.map((e) => (
          <div key={e.id} data-testid={`partenaire-${e.id}`} className="rounded-xl border border-slate-200 bg-white p-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-slate-900"><Building2 className="h-5 w-5 text-white" /></div>
                <div>
                  <div className="font-heading font-bold text-slate-900">{e.raison_sociale}</div>
                  <div className="text-xs text-slate-400">{e.ville}</div>
                </div>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${e.actif ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{e.statut_partenariat || (e.actif ? "Actif" : "Inactif")}</span>
            </div>
            <div className="mt-4 space-y-1.5 text-sm text-slate-600">
              <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-slate-400" /><span className="mono text-xs">{e.telephone}</span></div>
              <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-slate-400" /><span className="text-xs">{e.email}</span></div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2 border-t border-slate-100 pt-4 text-center">
              <div><div className="font-heading text-xl font-black text-slate-900">{e.nb_visites}</div><div className="text-[10px] uppercase text-slate-400">Dossiers</div></div>
              <div><div className="font-heading text-xl font-black text-orange-500">{e.nb_en_cours}</div><div className="text-[10px] uppercase text-slate-400">En cours</div></div>
              <div><div className="font-heading text-xl font-black text-emerald-600">{e.nb_terminees}</div><div className="text-[10px] uppercase text-slate-400">Terminés</div></div>
            </div>
            {soldes[e.id]?.prepayment_mode === "prepaiement" && (
              <div className="mt-3 rounded-lg bg-violet-50 p-3" data-testid={`solde-${e.id}`}>
                <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-violet-700">Prépaiement — jetons</div>
                {(soldes[e.id].soldes || []).length === 0 ? <div className="text-xs text-slate-400">Aucun jeton</div> : (
                  <div className="space-y-0.5">
                    {soldes[e.id].soldes.map((b) => (
                      <div key={b.vt_type} className="flex items-center justify-between text-xs">
                        <span className="text-slate-600">{b.vt_type}</span>
                        <span className={`mono font-semibold ${b.restant <= 3 ? "flex items-center gap-1 rounded bg-rose-100 px-1.5 text-rose-600 animate-pulse" : "text-violet-700"}`}>{b.restant} / {b.quantite_totale}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {soldes[e.id]?.prepayment_mode === "prepaiement" && (
              <div className="mt-3 flex gap-2">
                <button data-testid={`hist-btn-${e.id}`} onClick={() => setHistFor(e)} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">
                  <History className="h-3.5 w-3.5" /> Historique
                </button>
                {isAdmin && (
                  <button data-testid={`credit-btn-${e.id}`} onClick={() => setCreditFor(e)} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-violet-600 px-2 py-1.5 text-xs font-semibold text-white hover:bg-violet-700">
                    <PlusCircle className="h-3.5 w-3.5" /> Créditer
                  </button>
                )}
                {isAdmin && (
                  <button data-testid={`params-btn-${e.id}`} onClick={() => setParamsFor({ ...e, ...soldes[e.id] })} className="flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50" title="Reçus & alertes">
                    <Bell className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {histFor && <HistoryModal ent={histFor} onClose={() => setHistFor(null)} />}
      {creditFor && (
        <CreditModal ent={creditFor} vtTypes={vtTypes} onClose={() => setCreditFor(null)}
          onDone={() => { loadSolde(creditFor.id); setCreditFor(null); }} />
      )}
      {paramsFor && (
        <ParamsModal ent={paramsFor} onClose={() => setParamsFor(null)}
          onDone={() => { loadSolde(paramsFor.id); setParamsFor(null); }} />
      )}
    </div>
  );
}

function ParamsModal({ ent, onClose, onDone }) {
  const [email, setEmail] = useState(ent.email_contact || ent.email || "");
  const [seuil, setSeuil] = useState(ent.seuil_alerte_jetons ?? 5);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.patch(`/partenaires/${ent.id}/parametres`, { email_contact: email, seuil_alerte_jetons: Number(seuil) });
      toast.success("Paramètres enregistrés");
      onDone();
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <form onSubmit={submit} className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" data-testid="params-modal">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">Reçus & alertes — {ent.raison_sociale}</h2>
          <button type="button" onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Email de contact (reçu d'achat)</label>
            <input data-testid="params-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="contact@partenaire.fr" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Alerte quand il reste moins de (jetons)</label>
            <input data-testid="params-seuil" type="number" min="0" value={seuil} onChange={(e) => setSeuil(e.target.value)} className="w-28 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold hover:bg-slate-50">Annuler</button>
          <button data-testid="params-submit" disabled={loading} className="flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer
          </button>
        </div>
      </form>
    </div>
  );
}

function HistoryModal({ ent, onClose }) {
  const [movements, setMovements] = useState(null);
  useEffect(() => { api.get(`/partenaires/${ent.id}/mouvements`).then((r) => setMovements(r.data)).catch(() => setMovements([])); }, [ent.id]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl" data-testid="hist-modal">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">Historique des jetons — {ent.raison_sociale}</h2>
          <button onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button>
        </div>
        {!movements ? <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : movements.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400">Aucun mouvement</p>
        ) : (
          <table className="w-full text-left text-sm" data-testid="hist-table">
            <thead><tr className="border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
              <th className="py-2">Date</th><th className="py-2">Type VT</th><th className="py-2">Mouvement</th><th className="py-2">Solde</th><th className="py-2">Réf.</th>
            </tr></thead>
            <tbody>
              {movements.map((m) => (
                <tr key={m.id} className="border-b border-slate-100">
                  <td className="py-2 text-xs text-slate-500">{formatDateTime(m.date)}</td>
                  <td className="py-2 text-xs text-slate-700">{m.vt_type}</td>
                  <td className="py-2">
                    <span className={`flex items-center gap-1 text-xs font-semibold ${m.delta > 0 ? "text-emerald-600" : "text-amber-600"}`}>
                      {m.delta > 0 ? <ArrowUpCircle className="h-3.5 w-3.5" /> : <ArrowDownCircle className="h-3.5 w-3.5" />}
                      {m.delta > 0 ? `+${m.delta}` : m.delta}
                      {m.montant ? <span className="ml-1 text-slate-400">({m.montant.toLocaleString("fr-FR")} €)</span> : null}
                    </span>
                  </td>
                  <td className="py-2 mono text-xs font-semibold text-violet-700">{m.solde_apres}</td>
                  <td className="py-2 text-xs text-slate-400">{m.reference}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function CreditModal({ ent, vtTypes, onClose, onDone }) {
  const [form, setForm] = useState({ vt_type: vtTypes[0]?.nom || "", quantite: 10, reference: "Crédit manuel (admin)" });
  const [loading, setLoading] = useState(false);
  const selectedVt = vtTypes.find((t) => t.nom === form.vt_type);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/partenaires/${ent.id}/crediter`, { vt_type: form.vt_type, quantite: Number(form.quantite), reference: form.reference });
      toast.success("Jetons crédités");
      onDone();
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <form onSubmit={submit} className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" data-testid="credit-modal">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">Créditer des jetons — {ent.raison_sociale}</h2>
          <button type="button" onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Type de VT</label>
            <select data-testid="credit-vt" value={form.vt_type} onChange={(e) => setForm({ ...form, vt_type: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" required>
              <option value="">Sélectionner…</option>
              {vtTypes.map((t) => <option key={t.id} value={t.nom}>{t.nom} {t.prix != null ? `— ${t.prix} €` : ""}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Quantité de jetons</label>
            <input data-testid="credit-qte" type="number" min="1" required value={form.quantite} onChange={(e) => setForm({ ...form, quantite: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Référence</label>
            <input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </div>
          {selectedVt && selectedVt.prix != null && (
            <div className="rounded-lg bg-violet-50 p-3 text-sm text-violet-700">
              Montant total : <b>{(selectedVt.prix * Number(form.quantite || 0)).toLocaleString("fr-FR")} €</b>
            </div>
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold hover:bg-slate-50">Annuler</button>
          <button data-testid="credit-submit" disabled={loading} className="flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Créditer
          </button>
        </div>
      </form>
    </div>
  );
}
